import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form",
    "dayRow",
    "dayEnabled",
    "startTime",
    "endTime",
    "hoursDisplay",
    "saveButton",
    "customHoursFields",
    "blockedField",
    "overrideDate",
    "overrideStartTime",
    "overrideEndTime",
    "overrideError",
    "overrideSubmit",
    "listView",
    "calendarView",
    "listViewButton",
    "calendarViewButton",
    "overridesSection",
    "dayEditModalDate",
    "dayEditModalWeekday"
  ]

  connect() {
    console.log("Scheduling Availability controller connected")
    try {
      console.log("Day rows found:", this.hasDayRowTarget ? this.dayRowTargets.length : 0)
      this.setupFormChangeDetection()
      this.restoreViewFromUrl()
    } catch (error) {
      console.error("Error in availability controller connect:", error)
    }
  }

  // Restore view state from URL parameters
  restoreViewFromUrl() {
    const urlParams = new URLSearchParams(window.location.search)
    const view = urlParams.get('view')

    if (view === 'calendar') {
      // Trigger calendar view without event
      this.showCalendarView()
    }
  }

  // Show calendar view (without event)
  showCalendarView() {
    if (this.hasListViewTarget) {
      this.listViewTarget.style.display = 'none'
    }
    if (this.hasCalendarViewTarget) {
      this.calendarViewTarget.style.display = ''
    }
    if (this.hasOverridesSectionTarget) {
      this.overridesSectionTarget.style.display = 'none'
    }

    // Update button states
    if (this.hasListViewButtonTarget) {
      this.listViewButtonTarget.classList.remove('active')
    }
    if (this.hasCalendarViewButtonTarget) {
      this.calendarViewButtonTarget.classList.add('active')
    }
  }

  // Toggle day enabled/disabled
  toggleDay(event) {
    console.log("Toggle day called", event.target.checked)
    const checkbox = event.target
    const row = checkbox.closest('tr')
    const startTimeInput = row.querySelector('[data-scheduling--availability-target="startTime"]')
    const endTimeInput = row.querySelector('[data-scheduling--availability-target="endTime"]')
    const hoursDisplay = row.querySelector('[data-scheduling--availability-target="hoursDisplay"]')

    console.log("Found inputs:", { startTimeInput, endTimeInput, hoursDisplay })
    console.log("Start time disabled before:", startTimeInput?.disabled)
    console.log("End time disabled before:", endTimeInput?.disabled)

    if (checkbox.checked) {
      if (startTimeInput) {
        startTimeInput.disabled = false
        console.log("Set start time disabled to false")
      }
      if (endTimeInput) {
        endTimeInput.disabled = false
        console.log("Set end time disabled to false")
      }
      this.updateHoursDisplay(row)
    } else {
      if (startTimeInput) startTimeInput.disabled = true
      if (endTimeInput) endTimeInput.disabled = true
      if (hoursDisplay) hoursDisplay.textContent = '-'
    }

    console.log("Start time disabled after:", startTimeInput?.disabled)
    console.log("End time disabled after:", endTimeInput?.disabled)

    this.markFormAsChanged()
  }

  // Validate time ranges
  validateTime(event) {
    const row = event.target.closest('tr')
    const startTimeInput = row.querySelector('[data-scheduling--availability-target="startTime"]')
    const endTimeInput = row.querySelector('[data-scheduling--availability-target="endTime"]')

    if (!startTimeInput.value || !endTimeInput.value) return

    const startTime = this.parseTime(startTimeInput.value)
    const endTime = this.parseTime(endTimeInput.value)

    if (endTime <= startTime) {
      endTimeInput.setCustomValidity('End time must be after start time')
      endTimeInput.classList.add('is-invalid')
    } else {
      endTimeInput.setCustomValidity('')
      endTimeInput.classList.remove('is-invalid')
      this.updateHoursDisplay(row)
    }

    this.markFormAsChanged()
  }

  // Update hours display for a day
  updateHoursDisplay(row) {
    const checkbox = row.querySelector('[data-scheduling--availability-target="dayEnabled"]')
    const startTimeInput = row.querySelector('[data-scheduling--availability-target="startTime"]')
    const endTimeInput = row.querySelector('[data-scheduling--availability-target="endTime"]')
    const hoursDisplay = row.querySelector('[data-scheduling--availability-target="hoursDisplay"]')

    if (!checkbox.checked || !startTimeInput.value || !endTimeInput.value) {
      hoursDisplay.textContent = '-'
      return
    }

    const startTime = this.parseTime(startTimeInput.value)
    const endTime = this.parseTime(endTimeInput.value)
    const hours = (endTime - startTime) / (1000 * 60 * 60)

    if (hours > 0) {
      hoursDisplay.textContent = `${hours.toFixed(1)}h`
    } else {
      hoursDisplay.textContent = '-'
    }
  }

  // Parse time string to Date object
  parseTime(timeString) {
    const [hours, minutes] = timeString.split(':').map(Number)
    const date = new Date()
    date.setHours(hours, minutes, 0, 0)
    return date
  }

  // Quick action: Set business hours (9-5, Mon-Fri)
  setBusinessHours(event) {
    event.preventDefault()

    this.dayRowTargets.forEach((row, index) => {
      const checkbox = row.querySelector('[data-scheduling--availability-target="dayEnabled"]')
      const startTimeInput = row.querySelector('[data-scheduling--availability-target="startTime"]')
      const endTimeInput = row.querySelector('[data-scheduling--availability-target="endTime"]')

      // Enable Monday-Friday (1-5), disable Sat-Sun (0, 6)
      if (index >= 1 && index <= 5) {
        checkbox.checked = true
        startTimeInput.disabled = false
        endTimeInput.disabled = false
        startTimeInput.value = '09:00'
        endTimeInput.value = '17:00'
        this.updateHoursDisplay(row)
      } else {
        checkbox.checked = false
        startTimeInput.disabled = true
        endTimeInput.disabled = true
        this.updateHoursDisplay(row)
      }
    })

    this.markFormAsChanged()
  }

  // Quick action: Enable weekdays only
  enableWeekdays(event) {
    event.preventDefault()

    this.dayRowTargets.forEach((row, index) => {
      const checkbox = row.querySelector('[data-scheduling--availability-target="dayEnabled"]')
      const startTimeInput = row.querySelector('[data-scheduling--availability-target="startTime"]')
      const endTimeInput = row.querySelector('[data-scheduling--availability-target="endTime"]')

      // Enable Monday-Friday (1-5), disable Sat-Sun (0, 6)
      if (index >= 1 && index <= 5) {
        checkbox.checked = true
        startTimeInput.disabled = false
        endTimeInput.disabled = false
        this.updateHoursDisplay(row)
      } else {
        checkbox.checked = false
        startTimeInput.disabled = true
        endTimeInput.disabled = true
        this.updateHoursDisplay(row)
      }
    })

    this.markFormAsChanged()
  }

  // Reset form to original state
  resetForm(event) {
    event.preventDefault()

    if (confirm('Reset all changes? This will reload the page.')) {
      window.location.reload()
    }
  }

  // Toggle override type (custom hours vs blocked)
  toggleOverrideType(event) {
    const isBlocked = event.target.value === 'blocked'

    if (this.hasCustomHoursFieldsTarget) {
      this.customHoursFieldsTarget.style.display = isBlocked ? 'none' : 'block'
    }

    if (this.hasBlockedFieldTarget) {
      this.blockedFieldTarget.value = isBlocked ? '1' : '0'
    }

    // Clear validation
    if (this.hasOverrideStartTimeTarget) {
      this.overrideStartTimeTarget.required = !isBlocked
      this.overrideStartTimeTarget.setCustomValidity('')
    }

    if (this.hasOverrideEndTimeTarget) {
      this.overrideEndTimeTarget.required = !isBlocked
      this.overrideEndTimeTarget.setCustomValidity('')
    }
  }

  // Form change detection
  setupFormChangeDetection() {
    this.formChanged = false

    if (this.hasFormTarget) {
      this.formTarget.addEventListener('change', () => {
        this.markFormAsChanged()
      })
    }

    // Warn before leaving if there are unsaved changes
    window.addEventListener('beforeunload', (event) => {
      if (this.formChanged) {
        event.preventDefault()
        event.returnValue = ''
      }
    })
  }

  markFormAsChanged() {
    this.formChanged = true

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.add('btn-warning')
      this.saveButtonTarget.classList.remove('btn-admin-primary')
    }
  }

  // Switch to list view
  switchToListView(event) {
    event.preventDefault()

    if (this.hasListViewTarget) {
      this.listViewTarget.style.display = ''
    }
    if (this.hasCalendarViewTarget) {
      this.calendarViewTarget.style.display = 'none'
    }
    if (this.hasOverridesSectionTarget) {
      this.overridesSectionTarget.style.display = ''
    }

    // Update button states
    if (this.hasListViewButtonTarget) {
      this.listViewButtonTarget.classList.add('active')
    }
    if (this.hasCalendarViewButtonTarget) {
      this.calendarViewButtonTarget.classList.remove('active')
    }

    // Update URL to remove view parameter
    const url = new URL(window.location)
    url.searchParams.delete('view')
    url.searchParams.delete('year')
    url.searchParams.delete('month')
    window.history.pushState({}, '', url)
  }

  // Switch to calendar view
  switchToCalendarView(event) {
    event.preventDefault()
    this.showCalendarView()

    // Update URL to include view parameter
    const url = new URL(window.location)
    url.searchParams.set('view', 'calendar')
    window.history.pushState({}, '', url)
  }

  // Navigate to previous month
  navigateToPreviousMonth(event) {
    const year = event.currentTarget.dataset.year
    const month = event.currentTarget.dataset.month
    window.location.href = `${window.location.pathname}?view=calendar&year=${year}&month=${month}`
  }

  // Navigate to next month
  navigateToNextMonth(event) {
    const year = event.currentTarget.dataset.year
    const month = event.currentTarget.dataset.month
    window.location.href = `${window.location.pathname}?view=calendar&year=${year}&month=${month}`
  }

  // Open day edit modal
  openDayModal(event) {
    const date = event.currentTarget.dataset.date
    const weekday = event.currentTarget.dataset.weekday

    // Store the selected date and weekday for later use
    this.selectedDate = date
    this.selectedWeekday = weekday

    // Update modal content
    if (this.hasDayEditModalDateTarget) {
      this.dayEditModalDateTarget.textContent = `Date: ${new Date(date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}`
    }

    if (this.hasDayEditModalWeekdayTarget) {
      this.dayEditModalWeekdayTarget.textContent = `${weekday}s`
    }

    // Show the modal
    const modal = new bootstrap.Modal(document.getElementById('dayEditModal'))
    modal.show()
  }

  // Edit specific date (create override)
  editSpecificDate(event) {
    if (this.selectedDate) {
      // Pre-fill the override form with the selected date
      const overrideDateInput = document.querySelector('input[name="override[date]"]')
      if (overrideDateInput) {
        overrideDateInput.value = this.selectedDate
      }

      // Show the override modal
      const modal = new bootstrap.Modal(document.getElementById('overrideModal'))
      modal.show()
    }
  }

  // Edit all instances of weekday (edit default)
  editAllWeekday(event) {
    // Switch back to list view
    this.switchToListView(new Event('click'))

    // Find the corresponding day row
    const dayMap = {
      'Sunday': 0,
      'Monday': 1,
      'Tuesday': 2,
      'Wednesday': 3,
      'Thursday': 4,
      'Friday': 5,
      'Saturday': 6
    }

    const dayIndex = dayMap[this.selectedWeekday]
    if (dayIndex !== undefined && this.hasDayRowTarget) {
      const targetRow = this.dayRowTargets.find(row => {
        return row.dataset.day === dayIndex.toString()
      })

      if (targetRow) {
        // Scroll to the row and highlight it briefly
        targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' })
        targetRow.classList.add('table-warning')

        // Focus on the start time input
        const startTimeInput = targetRow.querySelector('[data-scheduling--availability-target="startTime"]')
        if (startTimeInput) {
          setTimeout(() => {
            startTimeInput.focus()
            targetRow.classList.remove('table-warning')
          }, 1000)
        }
      }
    }
  }
}
